/**
 * Axios instance — centralized API configuration
 * All API calls go through this instance
 * Uses HTTP-only cookies exclusively for auth
 * @module app/axios
 */

import axios from 'axios';
import i18n from '../i18n';

const rawApiUrl = import.meta.env.VITE_API_URL?.trim();

/**
 * Normalize API base URL:
 * - In development, default to '/api' so Vite proxy handles requests
 * - In production, also prefer '/api' unless explicitly overridden
 * - If user provides full URL, append /api if missing
 */
const normalizeApiUrl = (url) => {
  if (!url) return '/api';

  const cleaned = url.replace(/\/+$/, '');

  // If already relative
  if (cleaned.startsWith('/')) {
    return cleaned.endsWith('/api') ? cleaned : `${cleaned}/api`;
  }

  // Absolute URL
  return cleaned.endsWith('/api') ? cleaned : `${cleaned}/api`;
};

const API_URL = normalizeApiUrl(rawApiUrl);

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
    if (isFormData) {
      const headers = { ...(config.headers || {}) };
      delete headers['Content-Type'];
      delete headers['content-type'];
      config.headers = headers;
    }

    config.headers['Accept-Language'] = i18n.language || 'en';

    let sessionId = sessionStorage.getItem('afriBrightSessionId');
    if (!sessionId) {
      sessionId = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      sessionStorage.setItem('afriBrightSessionId', sessionId);
    }

    config.headers['X-Session-ID'] = sessionId;

    const requestUrl = String(config.url || '');
    const hasAuthHeader = Boolean(config.headers?.Authorization || config.headers?.authorization);

    if (!hasAuthHeader) {
      const isMvpRequest = requestUrl.includes('/mvp/');
      const token = isMvpRequest
        ? localStorage.getItem('mvp_token') || localStorage.getItem('token')
        : localStorage.getItem('token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor with retry logic for connection errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;

    // Only retry on connection errors (ECONNREFUSED, ETIMEDOUT, etc.) during startup
    const isConnectionError = 
      !error.response && 
      error.code && 
      ['ECONNREFUSED', 'ETIMEDOUT', 'ENETUNREACH', 'ENOTFOUND'].includes(error.code);

    const retries = config.__retries || 0;
    const maxRetries = 3;
    const delay = Math.min(1000 * Math.pow(2, retries), 5000); // Exponential backoff, max 5s

    if (isConnectionError && retries < maxRetries) {
      config.__retries = retries + 1;
      await new Promise((resolve) => setTimeout(resolve, delay));
      return api(config);
    }

    if (error.response?.status === 401) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    return Promise.reject(error);
  }
);

export default api;
